import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { planDocumentAPI } from '../../services/api'
import PlanMappingDialog from './PlanMappingDialog'
import { formatDate } from '../health-cycle/cycleStatus'
import { contentLanguage } from '../../i18n/supported'

const uiLanguage = contentLanguage
const WEEKEND = [6, 7]

function isoWeekday(isoDate) {
  const day = new Date(`${isoDate}T12:00:00`).getDay()
  return day === 0 ? 7 : day
}

// VH-229 refinement: log a meal of the stored weekly plan with one click — "Breakfast as planned"
// or the whole day. Ingredients without a food are mapped once, all together in one dialog (remembered for
// the plan); a plan meal already logged that day asks before logging again. Undo = delete the meal in the day's list.
export function PlanMealLogger({ date, onLogged, embedded = false }) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const language = uiLanguage(i18n.language)
  const [planId, setPlanId] = useState(null)
  const [pending, setPending] = useState(null) // { slots, force }
  const [mapping, setMapping] = useState(null) // { slots: [...] | null (whole plan), pending: {slots, force} | null }
  const [duplicate, setDuplicate] = useState(null)
  const [message, setMessage] = useState(null)

  const plans = useQuery({
    queryKey: ['planMeals', language],
    queryFn: () => planDocumentAPI.listForLogging(language).then((r) => r.data),
  })

  const plan = useMemo(() => {
    const list = plans.data ?? []
    if (planId) return list.find((p) => p.plan_id === planId) ?? null
    return list.find((p) => p.week_start <= date && date <= p.week_end) ?? list[0] ?? null
  }, [plans.data, planId, date])

  const log = useMutation({
    mutationFn: ({ slots, force }) => planDocumentAPI.logMeals({
      plan_id: plan.plan_id, log_date: date, slots, force,
      meal_names: Object.fromEntries(slots.map((s) => [s, t('planMeals.mealName', { meal: t(`weeklyPlan.slot.${s}`) })])),
    }),
    onSuccess: (response) => {
      setPending(null)
      setDuplicate(null)
      const kcal = response.data.meals.reduce((sum, m) => sum + (m.kcal || 0), 0)
      setMessage(t('planMeals.logged', { count: response.data.meals.length, kcal: Math.round(kcal) }))
      queryClient.invalidateQueries({ queryKey: ['planMeals'] })
      onLogged?.()
    },
    onError: (err, variables) => {
      const data = err?.response?.data ?? {}
      if (data.detail === 'ingredients_need_mapping') {
        setMapping({ slots: variables.slots, pending: variables })
      } else if (data.detail === 'already_logged') {
        setPending(variables)
        setDuplicate(data.meals ?? [])
      } else {
        setMessage(t('planMeals.errors.generic'))
      }
    },
  })

  const mappingsSaved = () => {
    const next = mapping?.pending
    setMapping(null)
    queryClient.invalidateQueries({ queryKey: ['planMeals'] })
    if (next) log.mutate(next)
    else setMessage(t('planMeals.bulk.saved'))
  }

  if (plans.isLoading || !(plans.data ?? []).length || !plan) return null

  const weekend = WEEKEND.includes(isoWeekday(date))
  const slots = plan.meals.map((m) => m.slot).filter((s) => s !== 'training')
  const hasTraining = plan.meals.some((m) => m.slot === 'training')
  const loggedToday = new Set(plan.logged.filter((e) => e.date === date).map((e) => e.slot))
  const busy = log.isPending

  return (
    <section className={embedded ? 'space-y-3' : 'bg-surface-container rounded-lg shadow-md border border-gray-200 p-4 space-y-3'} data-testid="plan-meal-logger">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className={embedded ? 'text-sm font-medium text-gray-800' : 'text-base font-semibold text-gray-900'}>{t('planMeals.title')}</h3>
        {(plans.data ?? []).length > 1 && (
          <select className="border rounded px-2 py-1 text-sm" aria-label={t('planMeals.choosePlan')}
            value={plan.plan_id} onChange={(e) => setPlanId(Number(e.target.value))}>
            {plans.data.map((p) => (
              <option key={p.plan_id} value={p.plan_id}>{t('planMeals.planOption', { start: formatDate(p.week_start, i18n.language), end: formatDate(p.week_end, i18n.language) })}</option>
            ))}
          </select>
        )}
      </div>
      {weekend && <p className="text-xs text-gray-600">{t('planMeals.weekendNote')}</p>}
      <div className="flex flex-wrap gap-2">
        {slots.map((slot) => (
          <button key={slot} type="button" disabled={busy} data-testid={`log-plan-${slot}`}
            className={`text-sm rounded-full border px-3 py-1 ${loggedToday.has(slot) ? 'border-green-600 text-green-700 bg-green-50' : 'border-gray-300 hover:bg-gray-100'}`}
            onClick={() => { setMessage(null); log.mutate({ slots: [slot], force: false }) }}>
            {loggedToday.has(slot) ? '✓ ' : ''}{t('planMeals.mealName', { meal: t(`weeklyPlan.slot.${slot}`) })}
          </button>
        ))}
        {hasTraining && (
          <button type="button" disabled={busy} className="text-sm rounded-full border border-teal-700 text-teal-800 px-3 py-1 hover:bg-teal-50"
            onClick={() => { setMessage(null); log.mutate({ slots: ['training'], force: false }) }}>
            {loggedToday.has('training') ? '✓ ' : ''}{t('planMeals.trainingAddition')}
          </button>
        )}
        <button type="button" disabled={busy} className="btn-primary text-sm" data-testid="log-plan-day"
          onClick={() => { setMessage(null); log.mutate({ slots, force: false }) }}>
          {t('planMeals.wholeDay')}
        </button>
        {plan.cycle_id && (
          <button type="button" disabled={busy} className="text-sm underline text-gray-700" data-testid="review-plan-mappings"
            onClick={() => { setMessage(null); setMapping({ slots: null, pending: null }) }}>
            {t('planMeals.reviewMappings')}
          </button>
        )}
      </div>

      {duplicate && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-2" role="alert">
          <p>{t('planMeals.alreadyLogged', { meals: duplicate.map((d) => t(`weeklyPlan.slot.${d.slot}`)).join(', ') })}</p>
          <div className="flex gap-3">
            <button type="button" className="btn-outline text-sm" onClick={() => log.mutate({ ...pending, force: true })}>
              {t('planMeals.logAgain')}
            </button>
            <button type="button" className="text-sm underline" onClick={() => { setDuplicate(null); setPending(null) }}>
              {t('mealPrep.actions.cancel')}
            </button>
          </div>
        </div>
      )}

      {mapping && plan.cycle_id && (
        <PlanMappingDialog cycleId={plan.cycle_id} planId={plan.plan_id} slots={mapping.slots} meals={plan.meals}
          logAfterSave={!!mapping.pending} onClose={() => setMapping(null)} onSaved={mappingsSaved} />
      )}

      {message && <p className="text-sm text-gray-700" role="status">{message}</p>}
    </section>
  )
}

export default PlanMealLogger
